import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { resolveContent, resolveVideos, DEFAULT_IMAGES } from "@/lib/siteImages";
import { Image as ImageIcon, Upload, RotateCcw, Loader2, Video as VideoIcon } from "lucide-react";

const SLOTS = [
  { key: "hero", label: "Fondo del Hero", hint: "Imagen de fondo de la portada" },
  { key: "car", label: "Carro (Entrega 01)", hint: "Chevrolet Spark Life 2015" },
  { key: "lote", label: "Lote (Entrega 02)", hint: "Lote 208 m² Acacías" },
];

const VIDEO_SLOTS = [
  { key: "video1", label: "Video 1", hint: "Vertical 1080×1920 (9:16)" },
  { key: "video2", label: "Video 2", hint: "Vertical 1080×1920 (9:16)" },
];

export default function SiteImagesManager() {
  const [images, setImages] = useState(DEFAULT_IMAGES);
  const [videos, setVideos] = useState({ video1: null, video2: null });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setImages(resolveContent(data));
      setVideos(resolveVideos(data));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("El archivo debe ser una imagen");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen supera el máximo de 5 MB");
      return;
    }
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/images/${key}`, fd);
      toast.success("Imagen actualizada");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const restore = async (key) => {
    setBusy(key);
    try {
      await api.delete(`/admin/images/${key}`);
      toast("Imagen restaurada por defecto");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const uploadVideo = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("El archivo debe ser un video (MP4/WebM)");
      return;
    }
    if (file.size > 60 * 1024 * 1024) {
      toast.error("El video supera el máximo de 60 MB");
      return;
    }
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/videos/${key}`, fd);
      toast.success("Video actualizado");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const restoreVideo = async (key) => {
    setBusy(key);
    try {
      await api.delete(`/admin/videos/${key}`);
      toast("Video eliminado");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 p-6 md:p-8" data-testid="site-images">
      <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
        <ImageIcon className="w-5 h-5 text-[#F0B91B]" /> Imágenes del sitio
      </h2>
      <p className="text-sm text-[#FDFBF7]/60 font-outfit mt-2">
        Sube tus propias fotos para el hero, el carro y el lote (máx. 5 MB, JPG/PNG/WebP).
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {SLOTS.map((s) => (
          <div key={s.key} className="rounded-2xl border border-[#F0B91B]/15 bg-[#051A0F]/60 overflow-hidden" data-testid={`image-slot-${s.key}`}>
            <div className="aspect-video bg-[#0A2818] overflow-hidden">
              <img src={images[s.key]} alt={s.label} className="w-full h-full object-cover" data-testid={`image-preview-${s.key}`} />
            </div>
            <div className="p-4">
              <div className="font-bebas text-lg text-[#FDFBF7] tracking-wide">{s.label}</div>
              <div className="text-xs text-[#FDFBF7]/50 font-outfit mb-3">{s.hint}</div>
              <div className="flex gap-2">
                <label className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-3 py-2 rounded-lg font-bebas tracking-wider text-sm cursor-pointer transition-colors">
                  {busy === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Subir
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={busy === s.key}
                    onChange={(e) => { upload(s.key, e.target.files?.[0]); e.target.value = ""; }}
                    data-testid={`image-input-${s.key}`}
                  />
                </label>
                <button
                  onClick={() => restore(s.key)}
                  disabled={busy === s.key}
                  title="Restaurar imagen por defecto"
                  className="inline-flex items-center gap-1 text-[#FDFBF7]/60 hover:text-[#E63946] px-3 py-2 rounded-lg text-sm font-outfit transition-colors"
                  data-testid={`image-restore-${s.key}`}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Video slots */}
      <div className="mt-8 pt-6 border-t border-[#F0B91B]/15">
        <h3 className="font-bebas text-xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
          <VideoIcon className="w-5 h-5 text-[#F0B91B]" /> Videos (encima de "Dos entregas")
        </h3>
        <p className="text-sm text-[#FDFBF7]/60 font-outfit mt-1">
          Dos videos verticales 1080×1920 (9:16). MP4/WebM, máx. 60 MB. Se muestran solo si los subes.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
          {VIDEO_SLOTS.map((s) => (
            <div key={s.key} className="rounded-2xl border border-[#F0B91B]/15 bg-[#051A0F]/60 overflow-hidden" data-testid={`video-slot-${s.key}`}>
              <div className="aspect-[9/16] max-h-[320px] bg-black flex items-center justify-center overflow-hidden">
                {videos[s.key] ? (
                  <video src={videos[s.key]} className="w-full h-full object-cover" muted loop autoPlay playsInline data-testid={`video-preview-${s.key}`} />
                ) : (
                  <div className="text-[#FDFBF7]/30 font-outfit text-sm flex flex-col items-center gap-2">
                    <VideoIcon className="w-8 h-8" /> Sin video
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="font-bebas text-lg text-[#FDFBF7] tracking-wide">{s.label}</div>
                <div className="text-xs text-[#FDFBF7]/50 font-outfit mb-3">{s.hint}</div>
                <div className="flex gap-2">
                  <label className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-3 py-2 rounded-lg font-bebas tracking-wider text-sm cursor-pointer transition-colors">
                    {busy === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Subir video
                    <input type="file" accept="video/*" className="hidden" disabled={busy === s.key}
                      onChange={(e) => { uploadVideo(s.key, e.target.files?.[0]); e.target.value = ""; }}
                      data-testid={`video-input-${s.key}`} />
                  </label>
                  <button onClick={() => restoreVideo(s.key)} disabled={busy === s.key} title="Quitar video"
                    className="inline-flex items-center gap-1 text-[#FDFBF7]/60 hover:text-[#E63946] px-3 py-2 rounded-lg text-sm font-outfit transition-colors"
                    data-testid={`video-restore-${s.key}`}>
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
